import { RotateCcw } from 'lucide-react';

import SectionOrderList from './SectionOrderList';

/**
 * The operator's per-run adjustments: what the bands say, and what order they
 * come in.
 *
 * ⚠ WHAT IS DELIBERATELY NOT HERE, AND WHY. The builder also accepts
 * `geo_verified`, `licensed_verified`, `guarantee_verified`, `years_verified`,
 * `rating` and `reviews_count` as overrides. Those are not styling — they
 * LICENSE FACTUAL CLAIMS. A tick box in a panel labelled "tweaks" that lets
 * someone assert a client is licensed, or type a star rating nobody earned, is
 * a way to put an unverified claim on a page for a real business and have it
 * look like a formatting choice. They stay where a human has to mean it.
 *
 * The three fields here are copy and contact. Each still passes the gate it
 * always did: a phone through LC-09, a CTA through TPL-03's unlicensed-
 * commitment check. Overriding a value has never been a way around a check.
 */

const FIELDS = [
  {
    key: 'cta_text',
    label: 'Button text',
    placeholder: 'Check In Online',
    help: 'What every call-to-action button says. Long text is capped rather than refused.',
  },
  {
    key: 'value_proposition',
    label: 'Hero sub-heading',
    placeholder: 'The line under the H1',
    help: 'The sentence under the headline. Falls back to the brand record.',
  },
  {
    key: 'phone',
    label: 'Phone',
    placeholder: '(530) 824-5701',
    help: 'Overrides the number found in the brand record, for this run only.',
  },
] as const;

export interface BuilderTweaksProps {
  overrides: Record<string, string>;
  onOverride: (key: string, value: string) => void;
  /** The template's own order, from GET /landing-pages/templates. */
  templateSections: string[];
  /** The operator's order. Empty means "the template's own". */
  sectionOrder: string[];
  onSectionOrder: (order: string[]) => void;
}

export default function BuilderTweaks({
  overrides,
  onOverride,
  templateSections,
  sectionOrder,
  onSectionOrder,
}: BuilderTweaksProps) {
  const order = sectionOrder.length ? sectionOrder : templateSections;
  const moved = sectionOrder.length > 0;

  const move = (from: number, to: number) => {
    if (to < 0 || to >= order.length) return;
    const next = [...order];
    const [item] = next.splice(from, 1);
    next.splice(to, 0, item);
    onSectionOrder(next);
  };

  return (
    <div className="space-y-5">
      <div className="space-y-3">
        {FIELDS.map((f) => (
          <div key={f.key}>
            <label
              className="block text-sm font-medium"
              htmlFor={`tweak-${f.key}`}
            >
              {f.label}
            </label>
            <input
              id={`tweak-${f.key}`}
              value={overrides[f.key] ?? ''}
              placeholder={f.placeholder}
              onChange={(e) => onOverride(f.key, e.target.value)}
              className="mt-1 w-full rounded border border-border bg-background px-3 py-2 text-sm"
            />
            <p className="mt-1 text-xs text-muted-foreground">{f.help}</p>
          </div>
        ))}
      </div>

      <div>
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium">Section order</span>
          {moved && (
            <button
              type="button"
              onClick={() => onSectionOrder([])}
              className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
            >
              <RotateCcw className="h-3 w-3" />
              Template order
            </button>
          )}
        </div>
        {/* ⚠ REORDER ONLY, NO ADD OR REMOVE, AND THE UI SHOULD NOT SUGGEST
            OTHERWISE. The server refuses a list that is not a permutation,
            because dropping a section removes copy slots the gates still
            expect and fails as if the copy were bad. Offering a delete
            control here would be offering something that comes back 422. */}
        <p className="mt-1 text-xs text-muted-foreground">
          The hero is always first. Move the bands below it; they cannot be
          added or removed, only reordered.
        </p>
        <SectionOrderList order={order} onMove={move} />
      </div>
    </div>
  );
}
